import type { Matrix4Like, QuaternionLike, Vector3Like } from './types'

export class Quaternion {
  readonly isQuaternion = true

  constructor(public x = 0, public y = 0, public z = 0, public w = 1) {}

  set(x: number, y: number, z: number, w: number): this {
    this.x = x; this.y = y; this.z = z; this.w = w
    return this
  }

  clone(): Quaternion {
    return new Quaternion(this.x, this.y, this.z, this.w)
  }

  copy(q: QuaternionLike): this {
    this.x = q.x; this.y = q.y; this.z = q.z; this.w = q.w
    return this
  }

  identity(): this {
    return this.set(0, 0, 0, 1)
  }

  setFromAxisAngle(axis: Vector3Like, angle: number): this {
    const halfAngle = angle / 2, s = Math.sin(halfAngle)
    this.x = axis.x * s
    this.y = axis.y * s
    this.z = axis.z * s
    this.w = Math.cos(halfAngle)
    return this
  }

  /** From the upper 3x3 of `m`, a pure (unscaled) rotation. */
  setFromRotationMatrix(m: Matrix4Like): this {
    const te = m.elements,
      m11 = te[0], m12 = te[4], m13 = te[8],
      m21 = te[1], m22 = te[5], m23 = te[9],
      m31 = te[2], m32 = te[6], m33 = te[10],
      trace = m11 + m22 + m33
    if (trace > 0) {
      const s = 0.5 / Math.sqrt(trace + 1.0)
      this.w = 0.25 / s
      this.x = (m32 - m23) * s
      this.y = (m13 - m31) * s
      this.z = (m21 - m12) * s
    } else if (m11 > m22 && m11 > m33) {
      const s = 2.0 * Math.sqrt(1.0 + m11 - m22 - m33)
      this.w = (m32 - m23) / s
      this.x = 0.25 * s
      this.y = (m12 + m21) / s
      this.z = (m13 + m31) / s
    } else if (m22 > m33) {
      const s = 2.0 * Math.sqrt(1.0 + m22 - m11 - m33)
      this.w = (m13 - m31) / s
      this.x = (m12 + m21) / s
      this.y = 0.25 * s
      this.z = (m23 + m32) / s
    } else {
      const s = 2.0 * Math.sqrt(1.0 + m33 - m11 - m22)
      this.w = (m21 - m12) / s
      this.x = (m13 + m31) / s
      this.y = (m23 + m32) / s
      this.z = 0.25 * s
    }
    return this
  }

  /** The rotation taking unit vector `vFrom` to unit vector `vTo`. */
  setFromUnitVectors(vFrom: Vector3Like, vTo: Vector3Like): this {
    let r = vFrom.x * vTo.x + vFrom.y * vTo.y + vFrom.z * vTo.z + 1
    if (r < 1e-8) {
      r = 0
      if (Math.abs(vFrom.x) > Math.abs(vFrom.z)) {
        this.x = -vFrom.y; this.y = vFrom.x; this.z = 0; this.w = r
      } else {
        this.x = 0; this.y = -vFrom.z; this.z = vFrom.y; this.w = r
      }
    } else {
      this.x = vFrom.y * vTo.z - vFrom.z * vTo.y
      this.y = vFrom.z * vTo.x - vFrom.x * vTo.z
      this.z = vFrom.x * vTo.y - vFrom.y * vTo.x
      this.w = r
    }
    return this.normalize()
  }

  dot(q: QuaternionLike): number {
    return this.x * q.x + this.y * q.y + this.z * q.z + this.w * q.w
  }

  length(): number {
    return Math.sqrt(this.x * this.x + this.y * this.y + this.z * this.z + this.w * this.w)
  }

  normalize(): this {
    let l = this.length()
    if (l === 0) {
      this.x = 0; this.y = 0; this.z = 0; this.w = 1
    } else {
      l = 1 / l
      this.x *= l; this.y *= l; this.z *= l; this.w *= l
    }
    return this
  }

  /** The inverse of a unit quaternion (its conjugate). */
  invert(): this {
    return this.conjugate()
  }

  conjugate(): this {
    this.x *= -1; this.y *= -1; this.z *= -1
    return this
  }

  multiply(q: QuaternionLike): this {
    return this.multiplyQuaternions(this, q)
  }

  premultiply(q: QuaternionLike): this {
    return this.multiplyQuaternions(q, this)
  }

  multiplyQuaternions(a: QuaternionLike, b: QuaternionLike): this {
    const qax = a.x, qay = a.y, qaz = a.z, qaw = a.w
    const qbx = b.x, qby = b.y, qbz = b.z, qbw = b.w
    this.x = qax * qbw + qaw * qbx + qay * qbz - qaz * qby
    this.y = qay * qbw + qaw * qby + qaz * qbx - qax * qbz
    this.z = qaz * qbw + qaw * qbz + qax * qby - qay * qbx
    this.w = qaw * qbw - qax * qbx - qay * qby - qaz * qbz
    return this
  }

  slerp(qb: QuaternionLike, t: number): this {
    if (t <= 0) return this
    if (t >= 1) return this.copy(qb)
    let x = qb.x, y = qb.y, z = qb.z, w = qb.w
    let dot = this.dot(qb)
    if (dot < 0) {
      x = -x; y = -y; z = -z; w = -w
      dot = -dot
    }
    let s = 1 - t
    if (dot < 0.9995) {
      const theta = Math.acos(dot)
      const sin = Math.sin(theta)
      s = Math.sin(s * theta) / sin
      t = Math.sin(t * theta) / sin
      this.x = this.x * s + x * t
      this.y = this.y * s + y * t
      this.z = this.z * s + z * t
      this.w = this.w * s + w * t
    } else {
      this.x = this.x * s + x * t
      this.y = this.y * s + y * t
      this.z = this.z * s + z * t
      this.w = this.w * s + w * t
      this.normalize()
    }
    return this
  }

  equals(q: QuaternionLike): boolean {
    return q.x === this.x && q.y === this.y && q.z === this.z && q.w === this.w
  }
}
